<template>
  <div class="patient-overview-pa-progress-progress">
    <v-row class="patient-overview-pa-progress-progress__header-row">
      <v-col cols="4">
      </v-col>
      <v-col cols="4" style="text-align: center;">
        <a :class="{'pull-left' : true, 'mb-10' : true, 'disabled' : !enablePrevChevron, 'patient-overview-pa-progress-progress__nextprev' : true}" @click="() => addWeeks(-1)">
          <v-icon class="patient-overview-pa-progress-progress__header-chevron">mdi-chevron-left</v-icon>
        </a>
        <span class="patient-overview-pa-progress-progress__header-dates">
          {{ startDateWeek ? startDateWeek.format('DD/MM/YYYY') : '' }} - {{ endDateWeek ? endDateWeek.format('DD/MM/YYYY') : '' }}
        </span>
        <a :class="{'pull-right' : true, 'mr-10' : true, 'disabled' : !enableNextChevron, 'patient-overview-pa-progress-progress__nextprev' : true}" @click="() => addWeeks(1)">
          <v-icon class="patient-overview-pa-progress-progress__header-chevron">mdi-chevron-right</v-icon>
        </a>
      </v-col>
      <v-col cols="4">
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" lg="6">
        <v-row class="border ma-2">
          <v-col>
            <h3 class="pa-2">{{  $t('myExcerciseProgressScreen.dailyActivity', 'app_mobile') }}</h3>

            <apexchart type="bar" height="350" :options="chartOptions" :series="series"></apexchart>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" lg="6">
        <v-row class="border ma-2">
          <v-col>
            <h3 class="pa-2">{{  $t('myExcerciseProgressScreen.weeklySports', 'app_mobile') }}</h3>
            <v-data-table
                class="simple-table"
                :headers="[{
                  value : 'weekDay',
                  text : $t('myExcerciseProgressScreen.weeklySports', 'app_mobile'),
                }]"
                item-key="id"
                :items="activityItems"
                :table-modifier-classes="['full-width']"
                :no-data-text="$t('addActivityState2Screen.noActivities', 'app_mobile')"
                disable-sort
                disable-filtering
                disable-pagination
                hide-default-footer
                hide-default-header
            >
              <template v-slot:item.weekDay="{ item }">
                <div class="patient-overview-pa-progress-progress__weekDay mt-2">
                  <h5>{{ item.weekDay }}</h5>

                  <ul class="ma-2">
                    <li v-for="(activity, index) in item.activities" :key="index">
                      <activity-icon :activityType="activity.key" />
                      {{ activity.duration }} {{ $t('pa.minimumShort').replace('.', '').toLowerCase() }}. {{ activity.name }} ({{ activity.calories }} {{ $t('pa.kcal') }})
                    </li>
                  </ul>
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
/**
 * Component PatientOverviewPAProgressProgress.vue
 *
 * @module PatientOverviewPAProgressProgress
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.7] app/src/APP_DASHBOARD/templates/views/Patient/PA/PatientOverviewPAProgressProgress.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

import moment from 'moment';
import gql from "graphql-tag";
import {mapGetters} from "vuex";
import ActivityIcon from "../../../components/Parts/ActivityIcon.vue";

export default {
  name: "PatientOverviewPAProgressProgress",
  components: {ActivityIcon},
  data: function () {
    return {
      data: null,
      selectedWeekMoment: moment(),
      loading: false,
      endOfThisWeek: moment().endOf('isoWeek')
    };
  },
  computed: {
    ...mapGetters({
      patientId: 'patientSummary/id',
    }),
    activityItems() {
      if (!this.data?.activityDays) {
        return [];
      }

      return this.data?.activityDays.reduce((acc, day) => {
        if (day?.activities.length) {
          acc.push({
            id : day.id,
            weekDay : day.date ? this.$ucfirst(moment.utc(day.date, "YYYY-MM-DD HH:mm:ss").format("dddd")) : null,
            activities : day.activities.map((activity) => {
              return {
                name : activity.patientActivityType.activityType.name,
                key : activity.patientActivityType.activityType.key,
                calories : activity.caloriesPlanned === null ? 0 : Math.round(activity.caloriesPlanned),
                duration : activity.duration
              }
            })
          })
        }
        return acc
      }, [])
    },
    series() {
      const data = this.data?.activityDays ? this.data.activityDays.map(day => day.stepsOutsideActivities ? day.stepsOutsideActivities : 0) : [0,0,0,0,0,0,0];

      return [
        {
          name: this.$t('stepCounterTool.stepsCounter', 'app_mobile'),
          data: data
        }
      ];
    },
   chartOptions () {
      const dayCategories = [];

     const startWeek = moment().startOf('isoWeek');

     for (let i = 0; i < 7; i++) {
       dayCategories.push(this.$ucfirst(startWeek.clone().add(i, 'days').format("dddd")));
     }

     return {
       colors: [this.$color.coro['purple']],
       chart: {
         type: "bar",
         height: 350,
         toolbar: {
           show: false,
         },
       },
       plotOptions: {
         bar: {
           horizontal: false,
           borderRadius: 10,
         }
       },
       dataLabels: {
         enabled: false
       },
       xaxis: {
         title: {
           text: this.$t('alerts.time')
         },
         categories: dayCategories
       },
       yaxis: {
         title: {
           text: this.$t('stepCounterTool.stepsCounter', 'app_mobile')
         }
       }
     }
    },
    startDateWeek() {
      return this.selectedWeekMoment ? this.selectedWeekMoment.clone().startOf('isoWeek') : null;
    },
    endDateWeek() {
      return this.selectedWeekMoment ? this.selectedWeekMoment.clone().endOf('isoWeek') : null;
    },
    enableNextChevron() {
      if (this.endDateWeek) {
        return this.endOfThisWeek.isAfter(this.endDateWeek);
      }else{
        return false;
      }
    },
    enablePrevChevron() {
      let importDate = this.$store.getters["patientSummary/importDate"];

      if (importDate) {
        const startOfImportDate = moment(importDate).startOf('isoWeek');
        return startOfImportDate.isBefore(this.startDateWeek);
      }

      return false;
    }
  },
  methods: {
    async loadData() {
      if (!(this.patientId && this.startDateWeek)) {
        this.data = null;
        return;
      }

      this.loading = true;

      this.$app.apolloMobileClient
          .query({
            query: gql`
              query getMyActivityWeeks($patientId: String!, $startDateWeek: String!) {
                myActivityWeeks(
                  conditions: {field: weekStartDate, value: $startDateWeek}
                  functions: [{ function: patientId, parameters: [$patientId] }]
                  orders: {field: activityDate, direction : asc}
                ){
                  pageInfo{
                    hasNextPage
                    hasPreviousPage
                  }
                  edges {    
                    node {
                      activityCount
                      activityDays {
                        id
                        date
                        steps
                        stepsOutsideActivities
                        activities {
                          patientActivityType {
                            activityType{
                              name
                              key
                            }
                          }
                          duration
                          caloriesPlanned
                        }
                      }
                    }
                  }
                }
              }
          `,
            variables: {
              patientId: this.patientId,
              startDateWeek : this.startDateWeek.format("YYYY-MM-DD HH:mm:ss"),
            },
          })
          .then((result) => {
            this.data = result.data.myActivityWeeks?.edges?.[0]?.node;
          })
          .finally(() => {
            this.loading = false;
          })
    },
    addWeeks(weeks) {
      this.selectedWeekMoment = this.selectedWeekMoment.clone().add(weeks, 'weeks');
    }
  },
  created() {
    this.loadData();
  },
  watch: {
    patientId() {
      this.loadData();
    },
    selectedWeekMoment() {
      this.loadData();
    },
  },
}
</script>

<style lang="scss">
.patient-overview-pa-progress-progress__header-row {
  font-weight: bold;
}
.patient-overview-pa-progress-progress__header-chevron.v-icon {
  font-size: 50px;
}
.patient-overview-pa-progress-progress__header-dates {
  line-height: 50px;
}
.patient-overview-pa-progress-progress__nextprev.disabled{
  opacity: 0.5;
  cursor: none;
  pointer-events: none;
}
.patient-overview-pa-progress-progress__weekDay {
  li {
    list-style: none;
  }
}
</style>
